<template>
  <div class="snackbar-multiple" :class="classes" :style="styles">
    <div class="snackbar-multiple__inner">
      <v-alert
        v-for="{ id, message, show, input, ...attrs } in queue"
        v-show="show"
        :key="id"
        class="snackbar-multiple__item"
        :value="show"
        v-bind="attrs"
        @input="removeItem({ id, message, input, ...attrs })"
      >
        <slot v-bind="{ input }">{{ message }}</slot>

        <template #close="scope">
          <slot name="close" v-bind="{ ...scope, input }">
            <v-btn text @click="scope.toggle()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </slot>
        </template>
      </v-alert>
    </div>
  </div>
</template>

<script>
// @input="removeItem({ id, message, input, ...attrs })"
import { convertToUnit } from 'vuetify/lib/util/helpers';

export default {
  name: 'SnackbarMultiple',

  inheritAttrs: false,

  props: {
    absolute: {
      type: Boolean,
      default: false
    },

    transition: {
      type: String,
      default: 'snackbar-multiple-transition'
      // default: 'fade-transition'
    },

    centered: {
      type: Boolean,
      default: false
    },

    top: {
      type: Boolean,
      default: false
    },

    right: {
      type: Boolean,
      default: false
    },

    bottom: {
      type: Boolean,
      default: false
    },

    left: {
      type: Boolean,
      default: false
    },

    timeout: {
      type: Number,
      default: 5000
    },

    delay: {
      type: Number,
      default: 150
    },

    maxItems: {
      type: Number,
      default: -1
    }
  },

  data: () => ({
    items: [],
    queue: []
  }),

  computed: {
    classes() {
      return {
        'snackbar-multiple_absolute': this.absolute,
        'snackbar-multiple_centered': this.centered,
        'snackbar-multiple_top': this.top,
        'snackbar-multiple_right': this.right,
        'snackbar-multiple_bottom': this.bottom,
        'snackbar-multiple_left': this.left
      };
    },
    styles() {
      // Styles are not needed when
      // using the absolute prop.
      if (this.absolute) {
        return {};
      }

      const {
        bar,
        bottom,
        footer,
        insetFooter,
        left,
        right,
        top
      } = this.$vuetify.application;

      // Should always move for y-axis
      // applicationable components.
      return {
        paddingBottom: convertToUnit(bottom + footer + insetFooter),
        paddingLeft: !this.app ? undefined : convertToUnit(left),
        paddingRight: !this.app ? undefined : convertToUnit(right),
        paddingTop: convertToUnit(bar + top)
      };
    },
    isMaxItems() {
      if (this.maxItems < 0) {
        return false;
      }

      return this.queue.length >= this.maxItems;
    },

    calculateDelay() {
      const delay = this.delay > 0 ? this.delay : 150;

      return this.queue.length * delay;
    }
  },

  mounted() {
    this.processItems();
  },

  methods: {
    processItems() {
      while (this.items.length && !this.isMaxItems) {
        const item = this.items.shift();
        const timeout = item.timeout || this.timeout;

        if (timeout > 0) {
          item.timeoutId = setTimeout(() => {
            this.removeItem(item);
          }, timeout + this.calculateDelay);
        }

        item.show = true;
        this.queue.push(item);
      }
    },

    removeItem(item) {
      item.show = false;
      clearTimeout(item.timeoutId);

      setTimeout(() => {
        this.queue = this.queue.filter(i => i.id !== item.id);
        this.$emit('remove', item.value);
        this.processItems();
      }, 150); // animation
    },

    addItem(value) {
      if (typeof value !== 'object') {
        return;
      }

      this.items.push({
        id: new Date().getTime() + Math.random(),
        color: 'info',
        message: '',
        transition: this.transition,
        input: value, // original data
        ...this.$attrs, // v-alert attrs
        ...value,
        show: false
      });

      this.processItems();
    }
  }
};
</script>

<style lang="scss">
.snackbar-multiple {
  width: 100%;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  pointer-events: none;

  &__inner {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    font-size: 0.875rem;
    overflow: hidden;
  }

  &:not(&_absolute) {
    height: 100vh;
    position: fixed;
    z-index: 1000;
  }

  &_absolute {
    height: 100%;
    position: absolute;
    z-index: 1;
  }

  &:not(&_top) &__inner {
    justify-content: flex-end;
  }

  &_top &__inner {
    justify-content: flex-start;
  }

  &_right &__inner {
    align-items: flex-end;
  }

  &_left &__inner {
    align-items: flex-start;
  }

  &_centered &__inner {
    align-items: center;
  }

  & &__item {
    padding: 6px 14px;
    margin: 8px;
    max-width: 672px;
    min-height: 48px;
    min-width: 344px;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
    pointer-events: auto;
    position: relative;
    transition-duration: 0.15s;
    transition-property: opacity, transform;
    transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
  }

  & .v-alert__icon {
    align-self: center;
  }

  &-transition {
    &-enter {
      &.snackbar-multiple__item {
        transform: scale(0.8);
      }

      .snackbar-multiple_left &.snackbar-multiple__item {
        transform: translateX(-50%);
      }

      .snackbar-multiple_right &.snackbar-multiple__item {
        transform: translateX(50%);
      }
    }

    &-enter,
    &-leave-to {
      &.snackbar-multiple__item {
        opacity: 0;
      }
    }
  }
}
</style>
